import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import FeatherIcon from 'feather-icons-react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Badge, message, Spin, Table } from 'antd';
import { closestCenter, DndContext } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
//
import DeleteImportedCar from './DeleteImportedCar';
import { Cards } from '../../../components/cards/frame/cards-frame';
import { UserTableStyleWrapper } from '../../pages/style';
import { TableWrapper } from '../../styled';
import {
  deleteImportedCar,
  getImportedCarIds,
  reFetchImportedCar,
  reOrderImportedCar,
  setAllImportedCarIdsData,
} from '../../../redux/carImport/carAmigo/actionCreator';
import { addImageFallback } from '../../../utility/utility';
import { Button } from '../../../components/buttons/buttons';
import { DragGrid } from '../../../components/utilities/icons';
import { importTargetPortals } from '../../../utils/caramigo';
import { getTopLevelDomain } from '../../../utils/constants';
import ReFetchImportedCar from './ReFetchImportedCar';

const ImportedCarListColumns = [
  {
    title: '',
    dataIndex: 'drag_action',
    key: 'drag_action',
  },
  {
    title: 'Image',
    dataIndex: 'image',
    key: 'image',
  },
  {
    title: 'Name',
    dataIndex: 'car_name',
    key: 'car_name',
  },
  {
    title: 'Imported To',
    dataIndex: 'importedTo',
    key: 'importedTo',
  },
  {
    title: 'status',
    dataIndex: 'status',
    key: 'status',
  },
  {
    title: 'Brand',
    dataIndex: 'brand',
    key: 'brand',
  },
  {
    title: 'Model',
    dataIndex: 'model',
    key: 'model',
  },
  {
    title: 'Price',
    dataIndex: 'price',
    key: 'price',
  },
  {
    title: 'Actions',
    dataIndex: 'actions',
    key: 'actions',
    width: '50',
    fixed: 'right',
  },
];

const SortableItemContext = createContext({
  attributes: {},
  listeners: undefined,
  ref() {},
});
const DragIcon = () => {
  const { attributes, listeners, ref } = useContext(SortableItemContext);

  return (
    <div ref={ref} {...listeners} {...attributes} style={{ cursor: 'grab', color: '#999' }}>
      <DragGrid icon="grid" size={14} />
    </div>
  );
};

const TableRow = ({ children, record, className, style, ...restProps }) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition } = useSortable({
    id: restProps['data-row-key'],
  });
  const context = useMemo(() => ({ attributes, listeners, ref: setActivatorNodeRef }), [
    attributes,
    listeners,
    setActivatorNodeRef,
  ]);

  return (
    <SortableItemContext.Provider value={context}>
      <tr
        ref={setNodeRef}
        className={`${className} custom-row`}
        style={{ ...style, transform: CSS.Transform.toString(transform), transition }}
        {...restProps}
      >
        {children}
      </tr>
    </SortableItemContext.Provider>
  );
};

const ImportedCarAmigo = ({ importedFilter }) => {
  const dispatch = useDispatch();
  const [deleteCar, setDeleteCar] = useState(false);
  const [refetchCar, setRefetchCar] = useState(false);
  const { apiLoading, allImportedCarIds } = useSelector(state => ({
    apiLoading: state.carAmigo.apiLoading,
    importedCarsTotal: state.carAmigo.importedCarsTotal,
    deletingCarId: state.carAmigo.deletingCarId,
    importedCarsReordering: state.carAmigo.importedCarsReordering,
    allImportedCarIds: state.carAmigo.allImportedCarIds,
  }));

  const topLevelDomain = getTopLevelDomain();
  const importablePortals = importTargetPortals(topLevelDomain);

  const handleImportedCarDelete = async (incomingId) => {
    const res = await dispatch(deleteImportedCar(incomingId));
    if (res.status === 200) {
      dispatch(getImportedCarIds(importedFilter));
    }
  };

  const handleDragEnd = async event => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = allImportedCarIds.findIndex(item => item._id === active.id);
    const newIndex = allImportedCarIds.findIndex(item => item._id === over.id);

    const newItems = arrayMove(allImportedCarIds, oldIndex, newIndex);
    dispatch(setAllImportedCarIdsData(newItems));
    const res = await dispatch(reOrderImportedCar(allImportedCarIds[oldIndex]._id, newIndex + 1));
    if (res?.status !== 200) {
      dispatch(getImportedCarIds(importedFilter));
      message.error('Failed to re order cars');
    }
  };

  const handleReFetch = async (incomingId) => {
    const res = await dispatch(reFetchImportedCar(incomingId));
    if (res.status === 200) {
      dispatch(getImportedCarIds(importedFilter));
    }
  };

  const importedCarListing = [];

  allImportedCarIds
    ?.filter(el => el?.meta)
    .map((car, index) => {
      const { image, brand, name, model, price } = car.meta;
      const _id = car?._id;
      const carId = car?.car?._id;

      const status = car?.car?.status;

      const importedToAuto24 = car?.importedPortals?.find(portal => portal?.portal === importablePortals?.auto24Portal)?.status === 'success';

      return importedCarListing.push({
        key: _id,
        drag_action: (
          <div style={{ display: 'flex', flexDirection: 'row', gap: '6px', alignItems: 'center' }}>
            <DragIcon id={_id} />
          </div>
        ),
        image: (
          <img
            src={image ? `${image}` : require(`../../../static/img/noimage.png`)}
            alt={`img${_id}`}
            onError={addImageFallback}
            height={50}
            width={50}
            style={{ objectFit: 'cover', borderRadius: '5px' }}
          />
        ),
        car_name: importedToAuto24 ? (
          <Link
            to={`/admin/cars/car-detail/${_id}`}
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {name}
          </Link>
        ) : (
          <span
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {name}
          </span>
        ),
        importedTo: (
          <span>
            {
              car?.importedPortals?.length > 0 ?
              car?.importedPortals.map(portal => <p className='m-0'>{portal.portal}: {portal.status}</p>)
              : '-'
            }
          </span>
        ),
        status: (
          <Badge
            style={{
              textTransform: 'capitalize',
              backgroundColor: car.status === 'sold' ? '#ff0000' : car.status === 'draft' ? '#155BDC' : '#0F4D0F',
            }}
            count={status}
          />
        ),
        brand: <span>{brand}</span>,
        model: <span>{model}</span>,
        price: (
          <span>
            {new Intl.NumberFormat('fr-FR').format(price)} {process.env.REACT_APP_DEFAULT_CURRENCY}
          </span>
        ),
        actions: (
          <div className="table-actions">
            <>
              {importedToAuto24 ? (
                <>
                  <Link to={`/admin/cars/car-detail/${carId}`} style={{ padding: '0' }}>
                    <Button className="btn-cion" type="info" to="#" shape="circle">
                      <FeatherIcon icon="eye" size={16} />
                    </Button>
                  </Link>
                  <Link to={`/admin/cars/edit/${carId}`} style={{ padding: '0' }}>
                    <Button className="btn-icon" type="info" to="#" shape="circle">
                      <FeatherIcon icon="edit" size={16} />
                    </Button>
                  </Link>
                </>
              ) : (
                <>
                  <Link to={`/admin/car-import/caramigo-meta/detail/${_id}`} style={{ padding: '0' }}>
                    <Button className="btn-cion" type="info" to="#" shape="circle">
                      <FeatherIcon icon="eye" size={16} />
                    </Button>
                  </Link>
                </>
              )}
              {
                car.importedPortals?.length > 0 &&
                car.importedPortals?.find(portal => portal.portal == importablePortals.africarPortal)?.status === 'failed' &&
                <Button className="btn-icon" type="danger" to="#" shape="circle" onClick={() => setRefetchCar(car)}>
                  <FeatherIcon icon="refresh-cw" size={16} />
                </Button>
              }
              
              <Button className="btn-icon" type="danger" to="#" shape="circle" onClick={() => setDeleteCar(car)}>
                <FeatherIcon icon="trash" size={16} />
              </Button>
            </>
          </div>
        ),
      });
    });

  useEffect(() => {
    dispatch(getImportedCarIds(importedFilter));
  }, [importedFilter]);

  return (
    <>
      <Cards headless>
        {apiLoading ? (
          <div className="spin">
            <Spin />
          </div>
        ) : (
          <UserTableStyleWrapper>
            <TableWrapper className="table-responsive">
              <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext
                  items={allImportedCarIds.map(el => ({ ...el, id: el._id }))}
                  strategy={verticalListSortingStrategy}
                >
                  <Table
                    dataSource={importedCarListing}
                    columns={ImportedCarListColumns}
                    pagination={false}
                    components={{
                      body: {
                        row: TableRow,
                      },
                    }}
                  />
                </SortableContext>
              </DndContext>
            </TableWrapper>
          </UserTableStyleWrapper>
        )}
      </Cards>
      <DeleteImportedCar
        visible={deleteCar !== null}
        car={deleteCar}
        handleDelete={handleImportedCarDelete}
        onCancel={() => setDeleteCar(null)}
      />
      <ReFetchImportedCar
        visible={refetchCar !== null}
        car={refetchCar}
        handleReFetch={handleReFetch}
        onCancel={() => setRefetchCar(null)}
      />
    </>
  );
};

export default ImportedCarAmigo;
